/**
 * Shared JWT utilities for portal-side JWT payload decoding.
 *
 * NOTE: This is client-side decoding only (no signature verification).
 * Signature verification happens server-side in the API Gateway.
 *
 * @see Issue #1099 — Admin portal role validation
 */

/**
 * Decode a JWT payload without signature verification.
 * Handles base64url encoding correctly (RFC 7515).
 *
 * @returns Parsed payload object, or null if decoding fails
 */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('[JWT] Invalid JWT format — expected 3 parts');
      return null;
    }

    const base64Url = parts[1];
    if (!base64Url) return null;

    // base64url → base64: replace URL-safe chars and normalize padding
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    if (padLength > 0) {
      base64 += '='.repeat(padLength);
    }

    const binary = atob(base64);
    const jsonPayload =
      typeof TextDecoder !== 'undefined'
        ? new TextDecoder('utf-8').decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)))
        : decodeURIComponent(
            binary
              .split('')
              .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
              .join(''),
          );

    return JSON.parse(jsonPayload) as T;
  } catch (error) {
    console.error('[JWT] Failed to decode JWT payload:', error);
    return null;
  }
}
